/**
 * HOUSE - Casa Completa
 * Integra todos los componentes de la democracia
 */

import { COMPONENTS } from '../utils/constants.js';
import SimpleComponent from './SimpleComponent.js';
import SubAreaComponent from './SubAreaComponent.js';

export default class House {
  constructor(scene) {
    this.scene = scene;
    this.components = this.createComponents();
  }

  /**
   * Crear todos los componentes de la casa
   * @returns {Map<string, Component>}
   */
  createComponents() {
    const components = new Map();

    // Componentes SIMPLES
    components.set('techo', new SimpleComponent(this.scene, COMPONENTS.TECHO));
    components.set('paredes', new SimpleComponent(this.scene, COMPONENTS.PAREDES));
    components.set('fundacion', new SimpleComponent(this.scene, COMPONENTS.FUNDACION));

    // Componentes CON SUB-ÁREAS
    components.set('garantias', new SubAreaComponent(this.scene, COMPONENTS.GARANTIAS));
    components.set('municipalidades', new SubAreaComponent(this.scene, COMPONENTS.MUNICIPALIDADES));

    return components;
  }

  /**
   * Obtener componente por ID
   * @param {string} componentId
   * @returns {Component|null}
   */
  getComponent(componentId) {
    return this.components.get(componentId) || null;
  }

  /**
   * Obtener todos los componentes
   * @returns {Array<Component>}
   */
  getAllComponents() {
    return Array.from(this.components.values());
  }

  /**
   * Obtener componentes críticos
   * @returns {Array<Component>}
   */
  getCriticalComponents() {
    return this.getAllComponents().filter(c => c.critical);
  }

  /**
   * Verificar si algún componente crítico está destruido
   * @returns {boolean}
   */
  hasCriticalFailure() {
    return this.getCriticalComponents().some(c => c.isAtMaxDamage());
  }

  /**
   * Obtener total de daños en toda la casa
   * @returns {number}
   */
  getTotalDamageCount() {
    return this.getAllComponents().reduce((total, component) => {
      return total + component.getDamageCount();
    }, 0);
  }

  /**
   * Reparar componente por ID
   * @param {string} componentId
   * @returns {number} Integridad recuperada
   */
  repairComponent(componentId) {
    const component = this.getComponent(componentId);
    if (component) {
      return component.repair();
    }
    return 0;
  }

  /**
   * Reparar sub-área de un componente
   * @param {string} componentId
   * @param {string} subAreaId
   * @returns {number} Integridad recuperada
   */
  repairSubArea(componentId, subAreaId) {
    const component = this.getComponent(componentId);
    if (component && component.type === 'multi') {
      return component.repairSubArea(subAreaId);
    }
    return 0;
  }

  /**
   * Renderizar toda la casa (placeholder)
   */
  render() {
    // TODO: Implementar renderizado visual completo
    console.log('🏛️ Renderizando casa completa');
    
    // Posiciones placeholder
    const positions = {
      techo: { x: 400, y: 100 },
      garantias: { x: 400, y: 200 },
      paredes: { x: 400, y: 300 },
      municipalidades: { x: 400, y: 400 },
      fundacion: { x: 400, y: 500 }
    };

    this.components.forEach((component, id) => {
      const pos = positions[id] || { x: 400, y: 300 };
      component.render(pos.x, pos.y);
    });
  }

  /**
   * Resetear todos los componentes
   */
  reset() {
    this.components.forEach(component => {
      component.repair();
    });
  }

  /**
   * Destruir la casa y todos sus componentes
   */
  destroy() {
    this.components.forEach(component => {
      component.destroy();
    });
    this.components.clear();
    this.scene = null;
  }
}
